<template>
    <div class="onboarding-quicktrial__signup">
        <preloader :display="loading" />
        <header>
            <p
                :class="[
                    'onboarding-quicktrial__signup-title',
                    isDesktop ? 'typographies-display-medium' : 'typographies-display-small',
                ]"
            >
                {{ $t.__('pages.onboarding.trial.OnboardingQuicktrial.title') }}
            </p>
        </header>
        <div>
            <!-- Sign with Google -->
            <el-form
                ref="form"
                :rules="rules"
                labelPosition="top"
                inlineMessage
                :model="form"
                class="onboarding-quicktrial__signup-form"
            >
                <el-form-item
                    :label="$t.__('common.labels.professional_email')"
                    disabled
                    prop="login"
                    :error="errors.get('login')"
                >
                    <CompassInput
                        v-model="form.login"
                        :size="componentSize"
                        type="email"
                        disabled
                    />
                </el-form-item>

                <!-- Company Name -->
                <el-form-item
                    :label="$t.__('common.labels.your_company_name')"
                    prop="name"
                    :error="errors.get('name')"
                >
                    <CompassInput v-model="form.name" :size="componentSize" />
                </el-form-item>

                <!-- Phone -->
                <el-form-item
                    :label="$t.__('common.labels.phone')"
                    prop="phone_number"
                    :error="errors.get('phone_number')"
                >
                    <CompassTelInput
                        :modelValue="form.phone_number"
                        :defaultCountry="form.phone_country.toLowerCase()"
                        :size="componentSize"
                        @validate="updatePhoneNumber"
                        @onBlur="onBlurTelInput"
                        @country-changed="updatePhoneCountry"
                    />
                </el-form-item>

                <!-- Mailing -->
                <el-form-item>
                    <CompassCheckbox
                        modelValue="mailing"
                        :label="$t.__('pages.onboarding.trial.OnboardingQuicktrial.mailing_label')"
                        :checked="form.mailing"
                        @change="
                            () => {
                                form.mailing = !form.mailing;
                            }
                        "
                    />
                </el-form-item>

                <p
                    class="onboarding-quicktrial__signup-form-terms typographies-paragraph-medium-regular"
                    v-html="termsLabelLegalInformation"
                />

                <CompassButton
                    :label="$t.__('common.actions.create_account')"
                    type="primary"
                    :size="componentSize"
                    class="onboarding-quicktrial__signup-form-item"
                    @click.prevent="submitForm('form', 'signup')"
                />
            </el-form>

            <p
                class="onboarding-quicktrial__signup-terms typographies-paragraph-small-regular"
                v-html="legalsTerms"
            />
        </div>
    </div>
</template>

<script>
import FormMixins from 'mixins/FormMixins';
import QuickTrialMixins from 'mixins/QuicktrialMixins';
import {
    checkNotBlank,
    checkedNotSellsyMail,
    checkContainNoNumber,
} from 'vcomponents/forms/rules/Rules';
import { isDesktop } from 'utils/global';

export default {
    mixins: [FormMixins, QuickTrialMixins],
    data() {
        return {
            isDesktop: isDesktop(),
            form: {
                name: '',
                lastname: this.$route.query.last_name,
                firstname: this.$route.query.first_name,
                phone_country: 'fr',
                phone_number: '',
                login: this.$route.query.email,
                password: null,
                mailing: false,
                terms: true,
                country: 'FR',
                lang: 'fr',
                'g-recaptcha': null,
                utm_source: undefined,
                utm_medium: undefined,
                utm_campaign: undefined,
                google: true,
            },
            loading: false,
            rules: {
                login: [
                    {
                        required: true,
                        message: this.$t.__('common.labels.please_enter_an_email_address'),
                    },
                    {
                        type: 'email',
                        message: this.$t.__('common.labels.please_enter_a_valid_email_address'),
                    },
                    {
                        validator: checkedNotSellsyMail,
                        message: this.$t.__('common.labels.please_enter_a_valid_email_address'),
                        trigger: 'blur',
                    },
                ],
                name: [
                    {
                        validator: checkNotBlank,
                        message: this.$t.__('common.labels.please_enter_a_company_name'),
                        trigger: ['blur', 'change'],
                    },
                ],
                firstname: [
                    {
                        validator: checkNotBlank,
                        message: this.$t.__('common.labels.please_enter_a_first_name'),
                        trigger: ['blur', 'change'],
                    },
                    {
                        validator: checkContainNoNumber,
                        message: this.$t.__(
                            'common.labels.the_firstname_must_not_include_a_number',
                        ),
                        trigger: ['blur', 'change'],
                    },
                ],
                lastname: [
                    {
                        validator: checkNotBlank,
                        message: this.$t.__('common.labels.please_enter_a_name'),
                        trigger: ['blur', 'change'],
                    },
                    {
                        validator: checkContainNoNumber,
                        message: this.$t.__('common.labels.the_name_must_not_include_a_number'),
                        trigger: ['blur', 'change'],
                    },
                ],
                phone_number: [
                    {
                        validator: checkNotBlank,
                        message: this.$t.__('common.labels.please_enter_a_valid_phone_number'),
                        trigger: ['blur', 'change'],
                    },
                ],
            },
        };
    },
    computed: {
        componentSize() {
            return this.isDesktop ? 'large' : 'medium';
        },
    },
    mounted() {
        window.addEventListener('resize', () => {
            this.isDesktop = isDesktop();
        });
    },
    methods: {
        updatePhoneNumber(phoneNumber) {
            this.onValidateTelInput({ isValid: phoneNumber.valid });
            this.form.phone_number = phoneNumber.valid ? phoneNumber.formatted : '';
        },
        updatePhoneCountry(country) {
            this.form.phone_country = country.iso2;
        },
    },
};
</script>

<style lang="scss" scoped>
.onboarding-quicktrial {
    &__signup {
        max-width: 520px;
        margin: 0 auto;

        &-title {
            text-align: center;
            margin: 0 auto var(--spacing-5);

            @media screen and (max-width: 1023px) {
                text-align: left;
            }
        }

        &-form {
            &-item {
                width: 100%;
            }

            &-terms {
                margin: var(--spacing-3) 0;
            }

            :deep() {
                .el-form-item__label {
                    font-weight: var(--font-weights-proxima-nova-0);
                    font-size: var(--font-size-17);
                    line-height: var(--line-heights-24);

                    @media screen and (max-width: 1023px) {
                        font: var(--font-families-proxima-nova);
                        font-weight: var(--font-weights-proxima-nova-0);
                        font-size: var(--font-size-14);
                        line-height: var(--line-heights-20);
                    }
                }

                .el-form-item__error {
                    line-height: var(--font-size-20);
                }

                .el-form-item__content {
                    .compass-input,
                    .compass-select {
                        width: 100%;
                    }
                }
            }
        }

        &-terms {
            color: var(--colors-common-text-standard-caption);
            margin-top: var(--spacing-6);
        }
    }
}
</style>
